import './Engagements.scss';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../../store/selectors';
import { GetEngagemets } from '../../../../slices/marketplace/timeline/thunks/get-timeline-engagements.thunk';
import { GetTimelineStages } from '../../../../slices/marketplace/timeline/thunks/get-timeline-stages.thunk';
import Helpers from '../../../../utils/helper';
import BootstrapTable, {
    TableColumnFilterProps,
} from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import filterFactory, {
    textFilter,
    dateFilter,
    selectFilter,
    FILTER_TYPES,
} from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-filter';
import paginationFactory from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-paginator';
import { TableFilter } from '../../../../components/Table/models/table-filter';
import { Button } from 'reactstrap';
import { Tooltip } from 'react-tooltip';
import moment from 'moment';
import sanitizeHtml from 'sanitize-html';
import { CreateTimelineModal } from '../../Modals/CreateTimelineModal/CreateTimelineModal';
import ReactDOMServer from 'react-dom/server';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import createNotification from '../../../../utils/createNotification';
import {
    EngagementProcessStatus,
    EngagementProcessStatusOptions,
    EngagementProcessStatuses,
    EngagementProcessTransitions,
} from '../../../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';
import {
    LevelOfServiceOptions,
    LevelOfService,
    LevelsOfService,
} from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import { Timeline } from '../../../../slices/marketplace/timeline/models/timeline.model';
import { resetCurrentTimeline } from '../../../../slices/marketplace/timeline/timeline.slice';
import { EngagementBuyer } from '../../../../slices/marketplace/timeline/models/engagement-buyer.model';
import { MarketplaceBuyer } from '../../../../slices/marketplace/buyer/models/buyer';
import { ExpandedText } from '../../../DataRequest/ExpandedText/ExpandedText';
import { ShowFirstThenFullTextOnHoverLabel } from '../../../../components/ShowFirstThenFullTextOnHoverLabel/ShowFirstThenFullTextOnHoverLabel';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';

const Engagements = () => {
    const dispatch = useDispatch() as any;
    const navigate = useNavigate();

    const axios = useStateSelector((state) => state.core.axios);
    const engagementsTable = useStateSelector(
        (state) => state.timelines.engagementTimelinesTable
    );

    const [page, setPage] = useState(1);
    const [totalSize, setTotalSize] = useState(1);
    const [sizePerPage, setSizePerPage] = useState(5);
    const [engagements, setEngagements] = useState<Timeline[]>([]);
    const [selectedEngagement, setSelectedEngagement] = useState<Timeline>();
    const [statusToBeSet, setStatusToBeSet] =
        useState<EngagementProcessStatus>();

    const [isSavingStatus, setIsSavingStatus] = useState(false);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    const [engagedBuyersOptions, setEngagedBuyersOptions] = useState([]);

    const defaultSorted: [{ dataField: any; order: any }] = [
        {
            dataField: 'id',
            order: 'asc',
        },
    ];
    const [currentSort, setCurrentSort] = useState<TableFilter>(
        new TableFilter({ filters: [], logic: '' }, page, sizePerPage, {
            dir: 'asc',
            field: 'id',
        })
    );

    const fetchEngagements = async (sort?: TableFilter) => {
        await dispatch(GetEngagemets(sort ?? currentSort));
    };

    const fetchStages = async () => {
        await dispatch(GetTimelineStages());
    };

    const initiate = async () => {
        initializeEngagedBuyersOptions();
        await fetchStages();
        await fetchEngagements();
        setIsLoading(false);
    };

    useEffect(() => {
        initiate().then();
    }, []);

    useEffect(() => {
        setEngagements(engagementsTable.timelines);
        setPage(engagementsTable.pageNumber);
        setTotalSize(engagementsTable.totalItems);
        setSizePerPage(engagementsTable.pageSize);
    }, [fetchEngagements]);

    const nestedFieldsMap = [
        {
            name: 'currentEvent',
            sortPath: 'currentEvent.stage',
            filterPath: 'currentEvent.stageName',
        },
        {
            name: 'engagementProcess.engagedBuyers',
            sortPath: 'engagementProcess.engagedBuyers.length',
            filterPath: 'engagementProcess.engagedBuyers.companyName',
        },
        {
            name: 'engagementProcess.practices',
            sortPath: 'engagementProcess.practices.practiceName',
            filterPath: 'engagementProcess.practices.practiceName',
            operator: 'eq',
        },
        {
            name: 'engagementProcess.practices',
            sortPath: 'engagementProcess.practices.practiceName',
            filterPath: 'engagementProcess.practices.practiceName',
            operator: 'eq',
        },
    ];

    const handleTableChange = (
        _type: any,
        newState: {
            page: number;
            sizePerPage: number;
            sortField: string;
            sortOrder: 'asc' | 'desc';
            filters: {
                [key: string]: {
                    filterVal: any;
                    filterType: string;
                    comparator: any;
                };
            };
        }
    ) => {
        const { page, sizePerPage, sortField, sortOrder } = newState;

        const nestedPropPath =
            nestedFieldsMap.find((nf) => nf.name == sortField)?.sortPath ??
            sortField;

        const updatedPostObj = new TableFilter(
            { filters: [], logic: '' },
            page,
            sizePerPage,
            { dir: sortOrder, field: nestedPropPath }
        );

        const arrayOfObj = Object.entries(newState.filters).map((e) => ({
            [e[0]]: e[1],
        }));

        if (arrayOfObj.length > 0) {
            arrayOfObj.forEach((element) => {
                let key = Object.keys(element);
                let values = newState.filters[key.toString()];

                if (
                    values.filterType != FILTER_TYPES.DATE ||
                    values.filterVal.date != null
                ) {
                    const nestedField = nestedFieldsMap.find(
                        (nf) => nf.name == key[0]
                    );
                    const field = nestedField?.filterPath ?? key[0];

                    let filterOperator =
                        values.filterType == FILTER_TYPES.DATE ||
                        values.filterType == FILTER_TYPES.SELECT
                            ? 'eq'
                            : 'contains';

                    if (nestedField?.operator) {
                        filterOperator = nestedField.operator;
                    }

                    let obj = {
                        Field: field,
                        Operator: filterOperator,
                        Value:
                            values.filterType == FILTER_TYPES.DATE
                                ? moment(values.filterVal.date).format(
                                      'MM.DD.YYYY'
                                  )
                                : values.filterVal,
                    };

                    updatedPostObj.filter.filters.push(obj);
                }
            });
        }

        setCurrentSort(updatedPostObj);
        fetchEngagements(updatedPostObj);
    };

    const launchUpdateStatusProcess = (
        status: EngagementProcessStatus,
        engagement: Timeline
    ) => {
        setSelectedEngagement(engagement);
        setStatusToBeSet(status);
    };

    const onStatusUpdateConfirmation = async () => {
        setIsSavingStatus(true);
        axios
            .put(
                `/api/marketplace/engagements/${selectedEngagement.id}/status`,
                statusToBeSet,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )
            .then((response) => {
                if (response.status === 200) {
                    createNotification(
                        'Engagement status updated successfully',
                        'success'
                    );
                } else {
                    createNotification(
                        'An unexpected error occurred  during Status update',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'An unexpected error occurred  during Status update',
                    'error'
                );
            })
            .finally(() => {
                fetchEngagements();
                setIsSavingStatus(false);
                setSelectedEngagement(undefined);
                setStatusToBeSet(undefined);
            });
    };

    const initializeEngagedBuyersOptions = () => {
        axios.get('api/Marketplace/MarketplaceBuyers/all').then((response) => {
            if (response.status === 200) {
                const mappedOptions = response.data
                    .sort((a: MarketplaceBuyer, b: MarketplaceBuyer) =>
                        a.userFullName.localeCompare(b.userFullName)
                    )
                    .reduce(
                        (filterOptionsObject: any, buyer: MarketplaceBuyer) => {
                            filterOptionsObject[
                                buyer.corporateAffiliationName
                            ] = buyer.corporateAffiliationName;
                            return filterOptionsObject;
                        },
                        {}
                    );

                setEngagedBuyersOptions(mappedOptions);
            }
        });
    };

    const textFilterProps = textFilter({
        placeholder: 'Name',
    }) as TableColumnFilterProps;

    const engagementProcessStatusFilterProps = selectFilter({
        options: EngagementProcessStatusOptions,
        placeholder: 'Status',
    }) as TableColumnFilterProps;

    const levelOfServiceFilterProps = selectFilter({
        options: LevelOfServiceOptions,
        placeholder: 'Type',
    }) as TableColumnFilterProps;

    const engagementBuyersFilterProps = selectFilter({
        options: engagedBuyersOptions,
        placeholder: 'Corporation',
    }) as TableColumnFilterProps;

    const getTooltipBuyersList = (buyers: EngagementBuyer[]) => {
        const content = (
            <div>
                {buyers.map((buyer) => {
                    return <div className="mb-1">{buyer.companyName}</div>;
                })}
            </div>
        );

        return content;
    };

    const engagementsColumns = [
        {
            dataField: 'id',
            text: 'Id',
            headerClasses: 'id-column-header',
            sort: true,
        },
        {
            dataField: 'engagementProcess.practices',
            text: 'Practice Name',
            headerClasses: 'practice-column-header',
            sort: true,
            filter: textFilterProps,
            formatter: (cellContent: any[]) => {
                const practiceNames = cellContent?.map((practice) => {
                    return practice.practiceName;
                });

                return cellContent == null || cellContent.length === 0 ? (
                    'NA'
                ) : (
                    <ShowFirstThenFullTextOnHoverLabel
                        items={
                            practiceNames
                        }></ShowFirstThenFullTextOnHoverLabel>
                );
            },
        },
        {
            dataField: 'engagementProcess.userName',
            text: 'Owner Representative',
            headerClasses: 'username-column-header',
            sort: true,
            filter: textFilterProps,
            formatter: (cellContent: string) =>
                !cellContent ? 'NA' : cellContent,
        },
        {
            dataField: 'engagementProcess.levelOfService',
            text: 'Type',
            headerClasses: 'type-column-header',
            sort: true,
            filter: levelOfServiceFilterProps,
            formatter: (
                cellContent: LevelOfService,
                row: any,
                index: number
            ) => {
                const value = LevelsOfService.find(
                    (item) => item.id === cellContent
                );
                return value ? (
                    <div className="d-flex justify-content-start" key={index}>
                        <span
                            id={'tab-info-' + index}
                            className="tooltip-target"
                            data-tooltip-html={sanitizeHtml(value.name)}>
                            {value.shortName}{' '}
                        </span>
                        <Tooltip
                            anchorId={'tab-info-' + index}
                            place="top"
                            className="tooltip-content place-top mt-m1"
                            classNameArrow="arrow"
                            offset={0}
                        />
                    </div>
                ) : (
                    'NA'
                );
            },
        },
        {
            dataField: 'engagementProcess.status',
            text: 'Status',
            headerClasses: 'status-column-header',
            sort: true,
            filter: engagementProcessStatusFilterProps,
            formatter: (cellContent: EngagementProcessStatus, row: Timeline) =>
                cellContent === EngagementProcessStatus.Terminated ||
                cellContent === EngagementProcessStatus.Completed ? (
                    EngagementProcessStatuses.find((e) => e.id === cellContent)
                        .name
                ) : (
                    <div className="customerStyle">
                        <select
                            className="selectCustomer"
                            value={cellContent}
                            onChange={(event) => {
                                launchUpdateStatusProcess(
                                    Number(
                                        event.target.value
                                    ) as EngagementProcessStatus,
                                    row
                                );
                            }}>
                            {[
                                ...EngagementProcessStatuses.filter((c) =>
                                    Array.from(
                                        EngagementProcessTransitions.get(
                                            cellContent
                                        )
                                    ).includes(c.id)
                                ),
                            ].map((item) => (
                                <option
                                    value={item.id}
                                    key={'contentLevel ' + item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </select>
                    </div>
                ),
        },
        {
            dataField: 'currentEvent',
            text: 'Current Stage / Event',
            headerClasses: 'stage-event-column-header',
            sort: true,
            filter: textFilterProps,
            formatter: (cellContent: TimelineEvent) => {
                return cellContent
                    ? `${cellContent?.stageName} / ${cellContent?.name}`
                    : 'NA';
            },
        },
        {
            dataField: 'engagementProcess.deadline',
            text: 'Deadline',
            sort: true,
            headerClasses: 'deadline-event-column-header',
            filter: dateFilter({
                placeholder: 'Enter Name',
            }) as TableColumnFilterProps,
            formatter: (cellContent: Date) => (
                <div>
                    {cellContent
                        ? moment(cellContent).format('M/D/YYYY')
                        : 'NA'}
                </div>
            ),
        },
        {
            dataField: 'engagementProcess.engagedBuyers',
            text: 'Engaged Buyers',
            sort: true,
            headerClasses: 'engaged-buyers-column-header',
            filter: engagementBuyersFilterProps,
            formatter: (cellContent: EngagementBuyer[], row: Timeline) => {
                const buyersCount = cellContent?.length;

                if (buyersCount) {
                    return (
                        <div className="engaged-buyers-cell">
                            {buyersCount > 1
                                ? `${buyersCount} Buyers`
                                : '1 Buyer'}
                            <InfoTooltip
                                idText={`engaged-buyers-list-${row.id}`}
                                text={''}
                                html={ReactDOMServer.renderToString(
                                    getTooltipBuyersList(cellContent)
                                )}
                            />
                        </div>
                    );
                } else {
                    return <div>{'No Buyers'}</div>;
                }
            },
        },
        {
            dataField: '',
            text: 'Actions',
            headerClasses: 'actions-column-header',
            classes: 'action-column-cell',
            headerFormatter: () => (
                <div className="d-flex w-100">
                    <div className="w-100">Actions</div>
                </div>
            ),
            formatter: (cellContent: any, row: Timeline) => {
                return (
                    <div className="engagement-buttons">
                        <Button
                            type="button"
                            className="btn btn-primary engagement-btn"
                            onClick={() => {
                                navigate(
                                    `/marketplace/admin/engagement/${
                                        row.engagementProcess.practices.find(
                                            (x) => x
                                        ).practiceId
                                    }`
                                );
                            }}>
                            Seller Dashboard
                        </Button>
                        <Button
                            type="button"
                            className="btn btn-primary engagement-btn"
                            onClick={() => {
                                dispatch(resetCurrentTimeline());
                                navigate(
                                    `/marketplace/admin/engagements/${row.id}`
                                );
                            }}>
                            Timeline
                        </Button>
                        <Button
                            type="button"
                            className="btn btn-primary engagement-btn"
                            onClick={() => {}}>
                            Fee Schedule
                        </Button>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="engagements">
            <div>
                <div className="mb-3">
                    <strong>Engagements</strong>
                    <Button
                        type="button"
                        className="btn btn-primary ml-3 right-btn"
                        onClick={() => setIsCreateModalVisible(true)}>
                        Define New Engagement
                    </Button>
                </div>
                <div
                    role="tabpanel"
                    id="panel-0"
                    aria-labelledby="tab-0"
                    aria-hidden="false">
                    <div className="card-body">
                        {isLoading ? (
                            Helpers.renderTableLoader()
                        ) : (
                            <BootstrapTable
                                striped
                                bordered
                                bootstrap4
                                remote
                                keyField="id"
                                data={engagements}
                                columns={engagementsColumns}
                                defaultSorted={defaultSorted}
                                filter={filterFactory()}
                                headerClasses="header-vetrical-align-top"
                                pagination={paginationFactory({
                                    page,
                                    sizePerPage,
                                    totalSize,
                                    tooltipTargetId:
                                        'timeline-templates-tooltip',
                                })}
                                onTableChange={handleTableChange}
                                noDataIndication="No Engagements to display"
                                rowStyle={{ height: '44px' }}
                            />
                        )}
                    </div>
                </div>
            </div>
            {isCreateModalVisible ? (
                <CreateTimelineModal
                    isVisible={isCreateModalVisible}
                    onClose={() => {
                        setIsCreateModalVisible(false);
                        fetchEngagements();
                    }}
                />
            ) : (
                []
            )}
            {statusToBeSet >= 0 && selectedEngagement ? (
                <ConfirmationModal
                    isVisible={statusToBeSet >= 0}
                    setIsVisible={() => {
                        fetchEngagements();
                        setIsSavingStatus(false);
                        setSelectedEngagement(undefined);
                        setStatusToBeSet(undefined);
                    }}
                    header="Update Engagement's Status"
                    confirmationText={`Are you sure you want to update the status of the engagement to ${
                        EngagementProcessStatuses.find(
                            (s) => s.id === statusToBeSet
                        )?.name
                    }?`}
                    onConfirm={() => onStatusUpdateConfirmation()}
                    params={undefined}
                    nextButtonText={undefined}
                    cancelButtonText={undefined}
                    isLoading={isSavingStatus}
                    closeOnConfirm={false}
                />
            ) : (
                []
            )}
        </div>
    );
};

export default Engagements;
